import { FormControl, FormGroup } from '@angular/forms';
import { PropertyMetadata } from '../../../properties/property-types';
import { RegisterElement } from '../../element-decorator';
import { PolygonMapItem } from '../polygon-map-item';

@RegisterElement()
export class Striping extends PolygonMapItem {
    public override TypeName: string = "Striping";
    public override ParentType: string | null = "ParkingLevel";
    public override ParentFormGroupName: string | null = "MapItems";

    constructor(formGroup: FormGroup) {
        super(formGroup, "Striping");
        
        if (!formGroup.contains('Pattern')) {
            formGroup.addControl('Pattern', new FormControl('diagonal'));
        }
        if (!formGroup.contains('StripingType')) {
            formGroup.addControl('StripingType', new FormControl('pedestrian'));
        }
        
        
    }

    public override draw(ctx: CanvasRenderingContext2D, offsetX: number = 0, offsetY: number = 0): void {
        if(this.points.length == 0) return;

        const scale = ctx.getTransform().a;
        const lineWidth = 2 / scale;

        // Draw the polygon with the striping pattern
        ctx.beginPath();
        ctx.moveTo(this.points[0].x + offsetX, this.points[0].y + offsetY);
        for (let i = 1; i < this.points.length; i++) {
            ctx.lineTo(this.points[i].x + offsetX, this.points[i].y + offsetY);
        }
        ctx.closePath();

        // Draw the stripes
        this.drawStripes(ctx, offsetX, offsetY);

    }

    private drawStripes(ctx: CanvasRenderingContext2D, offsetX: number, offsetY: number): void {
        const spacing = 0.6;
        const angle =  45;
        const pattern = this.formGroup.get('Pattern')?.value || 'diagonal';

        // Save current context state
        ctx.save();

        // Add a small inset to the clipping path to prevent edge artifacts
        const inset = 0.001; // 1mm inset to prevent edge bleeding
        
        // Calculate the center and vectors for inset
        const points = this.points.map(p => ({ x: p.x + offsetX, y: p.y + offsetY }));
        const center = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
        center.x /= points.length;
        center.y /= points.length;
        
        // Create inset points
        const insetPoints = points.map(p => {
            const dx = p.x - center.x;
            const dy = p.y - center.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            return {
                x: center.x + dx * (1 - inset / len),
                y: center.y + dy * (1 - inset / len)
            };
        });

        // Set up clipping with inset path
        ctx.beginPath();
        ctx.moveTo(insetPoints[0].x, insetPoints[0].y);
        for (let i = 1; i < insetPoints.length; i++) {
            ctx.lineTo(insetPoints[i].x, insetPoints[i].y);
        }
        ctx.closePath();
        ctx.clip();

        // Calculate bounds of the inset shape
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const point of insetPoints) {
            minX = Math.min(minX, point.x);
            minY = Math.min(minY, point.y);
            maxX = Math.max(maxX, point.x);
            maxY = Math.max(maxY, point.y);
        }

        // Set line properties
        ctx.strokeStyle = "#" + this.formGroup.get('FillColor')?.value;
        ctx.lineWidth = spacing / 2;
        ctx.lineCap = 'butt';

        const width = maxX - minX;
        const height = maxY - minY;
        const angleRad = (angle * Math.PI) / 180;
        
        // Calculate the diagonal length and number of lines needed
        const diagonalLength = Math.sqrt(width * width + height * height);
        const numLines = Math.ceil(diagonalLength / spacing) + 1; // Add 1 for safety

        // Draw the lines
        ctx.beginPath();
        
        // Calculate the perpendicular vector for the lines
        const perpX = -Math.sin(angleRad);
        const perpY = Math.cos(angleRad);
        
        // Calculate the line vector
        const lineX = Math.cos(angleRad);
        const lineY = Math.sin(angleRad);

        // Draw first set of parallel lines
        for (let i = -numLines; i <= numLines; i++) {
            const offset = i * spacing;
            
            // Calculate the base point for this line
            const baseX = center.x + lineX * offset;
            const baseY = center.y + lineY * offset;
            
            // Calculate endpoints ensuring they stay within bounds
            const x1 = baseX - perpX * diagonalLength/2;
            const y1 = baseY - perpY * diagonalLength/2;
            const x2 = baseX + perpX * diagonalLength/2;
            const y2 = baseY + perpY * diagonalLength/2;

            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
        }

        if (pattern === 'crosshatch') {
            // Draw second set of parallel lines at 90 degrees to the first set
            for (let i = -numLines; i <= numLines; i++) {
                const offset = i * spacing;
                
                // Calculate the base point for the perpendicular line
                const baseX = center.x + perpX * offset;
                const baseY = center.y + perpY * offset;
                
                // Calculate endpoints using the original line direction
                const x1 = baseX - lineX * diagonalLength/2;
                const y1 = baseY - lineY * diagonalLength/2;
                const x2 = baseX + lineX * diagonalLength/2;
                const y2 = baseY + lineY * diagonalLength/2;
                
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
            }
        }
        
        ctx.stroke();
        ctx.restore();
    }

    public override getPropertyMetadata(): PropertyMetadata[] {
        return [
            ...super.getPropertyMetadata(),
            {
                formControlName: 'Pattern',
                displayName: 'Pattern',
                inputType: { 
                    type: 'select',
                    options: [
                        { value: 'diagonal', label: 'Diagonal Lines' },
                        { value: 'crosshatch', label: 'Crosshatch' }
                    ]
                },
                order: 2,
                category: 'Appearance'
            },
            {
                formControlName: 'StripingType',
                displayName: 'Type',
                inputType: { 
                    type: 'select',
                    options: [
                        { value: 'pedestrian', label: 'Pedestrian Crossing' },
                        { value: 'noparking', label: 'No Parking Zone' },
                        { value: 'loading', label: 'Loading Zone' },
                        { value: 'hazard', label: 'Hazard Area' }
                    ]
                },
                order: 3,
                category: 'Properties'
            },
            {
                formControlName: 'StripingAngle',
                displayName: 'Angle',
                inputType: { 
                    type: 'number',
                    min: 0,
                    max: 360
                },
                description: 'Angle of striping lines in degrees',
                order: 4,
                category: 'Appearance'
            },
            {
                formControlName: 'StripingSpacing',
                displayName: 'Line Spacing',
                inputType: { 
                    type: 'dimension',
                    unit: 'meters'
                },
                order: 5,
                category: 'Appearance'
            }
        ];
    }

    public override default(): Striping {
        this.points = [
            { x: 0, y: 0 },
            { x: 2, y: 0 },
            { x: 2, y: 1 },
            { x: 0, y: 1 }
        ];
        return this;
    }

    public override clone(): Striping {
        return new Striping(this.formGroup);
    }

} 
